<svelte:options customElement={{
  tag: 'carousel-slider',
  shadow: 'none',
  props: {
    items: { reflect: true, type: 'String' },
    autoplay: { reflect: true, type: 'Boolean' },
    interval: { reflect: true, type: 'Number' },
    variant: { reflect: true, type: 'String' },
    overlaytitle: { reflect: true, type: 'String', attribute: 'overlay-title' },
    overlaysubtitle: { reflect: true, type: 'String', attribute: 'overlay-subtitle' },
    overlaybuttonlabel: { reflect: true, type: 'String', attribute: 'overlay-button-label' },
    overlaybuttonhref: { reflect: true, type: 'String', attribute: 'overlay-button-href' }
  }
}} />

<script>
  import { onMount, onDestroy } from 'svelte';
  import SvgIcon from './SvgIcon.svelte';

  /**
   * navi5：汎用カルーセル
   * @prop {string} items - JSON形式のアイテム一覧 '[{"src":"...", "alt":"...", "href":"..."}]'
   * @prop {boolean} autoplay - 自動再生
   * @prop {number} interval - 自動再生間隔(ms)
   * @prop {string} variant - バリアント: 'default' / 'kv'
   * @prop {string} overlaytitle - KVオーバーレイタイトル（HTML可）
   * @prop {string} overlaysubtitle - KVオーバーレイサブタイトル（HTML可）
   * @prop {string} overlaybuttonlabel - KVボタンラベル
   * @prop {string} overlaybuttonhref - KVボタンリンク先
   */
  export let items = '[]';
  export let autoplay = false;
  export let interval = 5000;
  export let variant = 'default';
  export let overlaytitle = '';
  export let overlaysubtitle = '';
  export let overlaybuttonlabel = '';
  export let overlaybuttonhref = '#';

  let parsedItems = [];
  let currentIndex = 0;
  let timer = null;
  let progressTimer = null;
  let progress = 0;

  $: isKv = variant === 'kv';

  $: {
    try {
      parsedItems = typeof items === 'string' ? JSON.parse(items) : (Array.isArray(items) ? items : []);
    } catch (e) {
      console.warn('Carousel: Invalid items JSON', e);
      parsedItems = [];
    }
  }

  function goTo(index) {
    currentIndex = index;
    progress = 0;
    resetTimer();
  }

  function prev() {
    currentIndex = currentIndex > 0 ? currentIndex - 1 : parsedItems.length - 1;
    progress = 0;
    resetTimer();
  }

  function next() {
    currentIndex = currentIndex < parsedItems.length - 1 ? currentIndex + 1 : 0;
    progress = 0;
    resetTimer();
  }

  function startTimer() {
    if (autoplay && parsedItems.length > 1) {
      progress = 0;
      if (isKv) {
        // KV用: プログレスバーアニメーション
        const step = 50; // 50msごとに更新
        progressTimer = setInterval(() => {
          progress += (step / interval) * 100;
          if (progress >= 100) {
            progress = 0;
            next();
          }
        }, step);
      } else {
        timer = setInterval(next, interval);
      }
    }
  }

  function resetTimer() {
    if (timer) clearInterval(timer);
    if (progressTimer) clearInterval(progressTimer);
    timer = null;
    progressTimer = null;
    startTimer();
  }

  onMount(() => {
    startTimer();
  });

  onDestroy(() => {
    if (timer) clearInterval(timer);
    if (progressTimer) clearInterval(progressTimer);
  });
</script>

<div class="mod-carousel" class:_kv={isKv}>
  <div class="mod-carousel-track" style="transform: translateX(-{currentIndex * 100}%)">
    {#each parsedItems as item, i}
      <div class="mod-carousel-slide">
        {#if isKv}
          <div class="mod-carousel-slide__bg">
            <picture>
							<source media="(min-width: 1025px)" srcset="{item.src}">
							<img src="{item.srcSp}" alt={item.alt || ''} width="" height="">
						</picture>
            <img src={item.src} alt={item.alt || ''} class="mod-carousel-slide__img-hidden" />
          </div>
        {:else if item.href}
          <a href={item.href}>
            <img src={item.src} alt={item.alt || ''} />
          </a>
        {:else}
          <img src={item.src} alt={item.alt || ''} />
        {/if}
      </div>
    {/each}
  </div>

  {#if isKv}
    <!-- KV テキストオーバーレイ -->
    <div class="mod-carousel-kv-overlay">
      <div class="mod-carousel-kv-overlay__inner">
        {#if overlaytitle}
          <p class="mod-carousel-kv-overlay__title">{@html overlaytitle}</p>
        {/if}
        {#if overlaysubtitle}
          <p class="mod-carousel-kv-overlay__subtitle">{@html overlaysubtitle}</p>
        {/if}
        {#if overlaybuttonlabel}
          <div class="mod-carousel-kv-overlay__btn-wrap">
            <a href={overlaybuttonhref} class="mod-kv-btn">
              <span class="_text">{overlaybuttonlabel}</span>
              <span class="_icon">
                <SvgIcon name="arrow_right" size="lg" />
              </span>
            </a>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if parsedItems.length > 1}
    {#if !isKv}
      <button type="button" class="mod-carousel-prev" on:click={prev} aria-label="前へ">
        <SvgIcon name="arrow_left" size="lg" />
      </button>
      <button type="button" class="mod-carousel-next" on:click={next} aria-label="次へ">
        <SvgIcon name="arrow_right" size="lg" />
      </button>
    {/if}

    <div class="mod-carousel-dots" class:_kv-dots={isKv}>
      {#each parsedItems as _, i}
        <button
          type="button"
          class="mod-carousel-dot"
          class:is-active={currentIndex === i}
          class:_kv-dot={isKv}
          on:click={() => goTo(i)}
          aria-label="スライド {i + 1}"
        >
          {#if isKv && currentIndex === i}
            <span class="mod-carousel-dot__progress" style="width: {progress}%"></span>
          {/if}
        </button>
      {/each}

      <button type="button" class="mod-carousel-stop-btn" on:click={() => {
        if (autoplay) {
          autoplay = false;
          if (timer) clearInterval(timer);
          if (progressTimer) clearInterval(progressTimer);
        } else {
          autoplay = true;
          // タイマー再開時はprogressをリセットしない
          if (timer) clearInterval(timer);
          if (progressTimer) clearInterval(progressTimer);
          if (autoplay && parsedItems.length > 1) {
            if (isKv) {
              const step = 50;
              progressTimer = setInterval(() => {
                progress += (step / interval) * 100;
                if (progress >= 100) {
                  progress = 0;
                  next();
                }
              }, step);
            } else {
              timer = setInterval(next, interval * (1 - progress / 100) || interval);
            }
          }
        }
      }} aria-label={autoplay ? "自動再生停止" : "自動再生開始"}>
        <SvgIcon name={autoplay ? "stop" : "play"} size="lg" />
      </button>
    </div>
  {/if}
</div>

<style>
  .mod-carousel {
    position: relative;
    overflow: hidden;
    width: 100%;
  }

  .mod-carousel-track {
    display: flex;
    transition: transform 0.5s ease;
  }

  .mod-carousel-slide {
    flex: 0 0 100%;
    min-width: 100%;
  }

  .mod-carousel-slide img {
    display: block;
    width: 100%;
    height: auto;
  }

  .mod-carousel-prev,
  .mod-carousel-next {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    border: none;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    cursor: pointer;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.3s;
    z-index: 1;
  }

  .mod-carousel-prev { left: 10px; }
  .mod-carousel-next { right: 10px; }

  .mod-carousel-prev:hover,
  .mod-carousel-next:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  .mod-carousel-dots {
    position: absolute;
    bottom: 15px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
  }

  .mod-carousel-dot {
    width: 10px;
    height: 10px;
    border: none;
    background: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: 0.3s;
    padding: 0;
  }

  .mod-carousel-dot.is-active {
    background: #fff;
  }

  /* ================================
     KV バリアント
     ================================ */
  .mod-carousel._kv {
    width: 100%;
    height: 100vh;
    max-height: 540px;
  }

  .mod-carousel._kv .mod-carousel-track {
    height: 100%;
  }

  .mod-carousel._kv .mod-carousel-slide {
    height: 100%;
  }

  .mod-carousel._kv .mod-carousel-slide__bg {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    object-position: 50% 50%;
  }

  .mod-carousel._kv .mod-carousel-slide__bg picture {
    width: 100%;
  }

  .mod-carousel._kv .mod-carousel-slide__img-hidden {
    display: none;
  }

  /* KV ドットインジケーター: 横長バー型 */
  .mod-carousel-dots._kv-dots {
    bottom: 40px;
    left: 50%;
    align-items: center;
    transform: translateX(-50%);
    gap: 10px;
    z-index: 2;
  }

  .mod-carousel-dot._kv-dot {
    width: 70px;
    height: 3px;
    background: rgba(255, 255, 255, 0.35);
    position: relative;
    overflow: hidden;
  }

  .mod-carousel-dot._kv-dot.is-active {
    background: rgba(255, 255, 255, 0.35);
  }

  .mod-carousel-dot__progress {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: #fff;
    transition: width 0.05s linear;
  }

  @media (max-width: 1024px) {
    .mod-carousel-dots._kv-dots {
      gap: 8px;
      bottom: 10px;
    }
  }

  @media (max-width: 767px) {
    .mod-carousel._kv {
      height: 100svh;
      max-height: 420px;
    }
  }
</style>
